import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Tesseract from 'tesseract.js'
import {
  BadgeCheck,
  Camera as CameraAltIcon,
  Check as CheckIcon,
  CircleCheck as CheckCircleIcon,
  CircleX as CancelIcon,
  Eye as VisibilityIcon,
  RefreshCw as RefreshIcon,
  ScanLine,
  Search as SearchIcon,
  Upload as UploadIcon,
  UserRound as PersonIcon,
  X as CloseIcon,
} from 'lucide-react'

import {
  Button,
  Card,
  CardHeader,
  DataTable,
  FilterBar,
  IconButton,
  Modal,
  Notice,
  PersonCell,
  SearchInput,
  Skeleton,
  Spinner,
  StatusPill,
  Tabs,
  Textarea,
} from '../ui'

import {
  searchUsersForKYC,
  getPendingKYC,
  getKYCDetails,
  uploadKYCDocument,
  submitManualKYC,
  approveManualKYC,
  rejectManualKYC
} from '../actions/manualKYCActions'
import { GET_KYC_DETAILS_RESET } from '../constants/manualKYCConstants'
import { handleUploadImageError, resolveUploadUrl } from '../utils/uploadUrl'
import ScreenHeader from '../components/layout/ScreenHeader'

const DOC_TYPES = [
  { key: 'pan', label: 'PAN card' },
  { key: 'aadhaar_front', label: 'Aadhaar front' },
  { key: 'aadhaar_back', label: 'Aadhaar back' },
]

const capitalize = (value = '') => value.charAt(0).toUpperCase() + value.slice(1)

const VerifiedPill = ({ verified }) => (
  <StatusPill tone={verified ? 'success' : 'neutral'} size="sm">
    {verified ? 'Verified' : 'Not verified'}
  </StatusPill>
)

const ManualKYC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // States
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('search') // 'search', 'pending', 'scan'
  const [selectedUser, setSelectedUser] = useState(null)
  const [currentDocType, setCurrentDocType] = useState('pan') // 'pan', 'aadhaar_front', 'aadhaar_back'
  const [scannedDocs, setScannedDocs] = useState({
    pan: { image: null, uploaded: false, extractedName: null },
    aadhaar_front: { image: null, uploaded: false, extractedName: null },
    aadhaar_back: { image: null, uploaded: false, extractedName: null }
  })
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [viewingUser, setViewingUser] = useState(null)
  const [ocrProcessing, setOcrProcessing] = useState(false)

  // Refs
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  // Redux selectors
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { getRoleData } = getRoleInfo

  const searchUsersKYC = useSelector((state) => state.searchUsersKYC)
  const { loading: searchLoading, users: searchResults, error: searchError } = searchUsersKYC || {}

  const pendingKYC = useSelector((state) => state.pendingKYC)
  const { loading: pendingLoading, requests: pendingRequests, error: pendingError } = pendingKYC || {}

  const kycDetails = useSelector((state) => state.kycDetails)
  const { loading: detailsLoading, details: kycDetailsData } = kycDetails || {}

  // Check permissions
  useEffect(() => {
    if (getRoleData) {
      if (!getRoleData.manualKYC && !getRoleData.isSuperAdmin) {
        toast('Not Authorized', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
        navigate('/dashboard')
      }
    }
  }, [getRoleData, navigate])

  // Fetch pending KYC and initial users on mount
  useEffect(() => {
    dispatch(getPendingKYC())
    dispatch(searchUsersForKYC('')) // Fetch recent users
  }, [dispatch])

  // Handle search
  const handleSearch = () => {
    dispatch(searchUsersForKYC(searchQuery))
  }

  // Handle user selection for scanning
  const handleSelectUser = (user) => {
    setSelectedUser(user)
    setActiveTab('scan')
    setScannedDocs({
      pan: { image: null, uploaded: false, extractedName: null },
      aadhaar_front: { image: null, uploaded: false, extractedName: null },
      aadhaar_back: { image: null, uploaded: false, extractedName: null }
    })
  }

  // Extract name from OCR text based on document type
  const extractNameFromText = (text, docType) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0)
    
    if (docType === 'pan') {
      // PAN card format: Look for "Name" label followed by the name
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].toUpperCase()
        // Check if line contains "NAME" (either as label or section)
        if (line.includes('NAME') && !line.includes('FATHER')) {
          // The name is typically on the next line or same line after "Name"
          const nameMatch = line.match(/NAME\s*[:\s]+(.+)/i)
          if (nameMatch && nameMatch[1].trim().length > 2) {
            return nameMatch[1].trim()
          }
          // Check next line
          if (i + 1 < lines.length) {
            const nextLine = lines[i + 1].trim()
            // Name should be alphabetic characters
            if (/^[A-Za-z\s]+$/.test(nextLine) && nextLine.length > 2) {
              return nextLine
            }
          }
        }
      }
      // Alternative: look for all-caps name pattern (common in PAN)
      for (const line of lines) {
        if (/^[A-Z\s]{5,}$/.test(line) && !line.includes('INDIA') && !line.includes('INCOME') && !line.includes('PERMANENT')) {
          return line.trim()
        }
      }
    } else if (docType === 'aadhaar_front') {
      // Aadhaar front: Name is usually a prominent line in readable format
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        // Look for name-like patterns (capitalized words, not numbers)
        if (/^[A-Za-z\s]{4,}$/.test(line) && 
            !line.toUpperCase().includes('GOVERNMENT') && 
            !line.toUpperCase().includes('INDIA') &&
            !line.toUpperCase().includes('AADHAAR') &&
            !line.toUpperCase().includes('UNIQUE')) {
          return line.trim()
        }
      }
      // Look for hindi/english name pattern
      for (const line of lines) {
        const match = line.match(/^[A-Z][a-z]+(\s+[A-Z][a-z]+)+$/)
        if (match) {
          return line.trim()
        }
      }
    } else if (docType === 'aadhaar_back') {
      // Aadhaar back typically has address, but sometimes name
      for (const line of lines) {
        if (/^[A-Za-z\s]{4,}$/.test(line) && 
            !line.toUpperCase().includes('ADDRESS') &&
            !line.toUpperCase().includes('VID')) {
          return line.trim()
        }
      }
    }
    
    return null
  }

  // Handle file/camera capture - with OCR for name extraction
  const handleCapture = async (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = async (event) => {
        const imageData = event.target.result
        
        // Store image immediately
        setScannedDocs(prev => ({
          ...prev,
          [currentDocType]: { ...prev[currentDocType], image: imageData, uploaded: false }
        }))
        
        toast.info('Processing document for name extraction...')
        setOcrProcessing(true)
        
        try {
          // Run OCR on the captured image
          const result = await Tesseract.recognize(imageData, 'eng', {
            logger: (m) => {
              if (m.status === 'recognizing text') {
                // Optional: could show progress percentage
              }
            }
          })
          
          const extractedText = result.data.text
          console.log('OCR Extracted Text:', extractedText)
          
          // Extract name from the OCR text
          const extractedName = extractNameFromText(extractedText, currentDocType)
          
          if (extractedName) {
            setScannedDocs(prev => ({
              ...prev,
              [currentDocType]: { ...prev[currentDocType], extractedName: extractedName }
            }))
            toast.success(`Name extracted: ${extractedName}`)
          } else {
            toast.warning('Could not automatically extract name. Please verify manually.')
          }
        } catch (ocrError) {
          console.error('OCR Error:', ocrError)
          toast.warning('Name extraction failed. Please enter manually if needed.')
        } finally {
          setOcrProcessing(false)
        }
        
        toast.success(`${currentDocType.replace('_', ' ').toUpperCase()} captured successfully!`)
      }
      reader.readAsDataURL(file)
    }
  }

  // Upload documents to backend (with extracted name data)
  const handleUploadDocument = async (docType) => {
    const doc = scannedDocs[docType]
    if (!doc.image) {
      toast.error('Please capture the document first')
      return
    }

    try {
      // Send extracted name along with the image
      const extractedData = doc.extractedName ? { name: doc.extractedName } : null
      await dispatch(uploadKYCDocument(
        selectedUser._id,
        docType,
        extractedData,
        doc.image
      ))
      toast.success(`${docType.replace('_', ' ').toUpperCase()} uploaded successfully!`)
    } catch (error) {
      toast.error('Failed to upload document')
    }
  }

  // Submit all documents for verification (with extracted names)
  const handleSubmitKYC = async () => {
    if (!scannedDocs.pan.image) {
      toast.error('Please capture PAN card first')
      return
    }
    if (!scannedDocs.aadhaar_front.image || !scannedDocs.aadhaar_back.image) {
      toast.error('Please capture both sides of Aadhaar card')
      return
    }

    try {
      // Upload all documents with extracted name data
      const panData = scannedDocs.pan.extractedName ? { name: scannedDocs.pan.extractedName } : null
      const aadhaarFrontData = scannedDocs.aadhaar_front.extractedName ? { name: scannedDocs.aadhaar_front.extractedName } : null
      const aadhaarBackData = scannedDocs.aadhaar_back.extractedName ? { name: scannedDocs.aadhaar_back.extractedName } : null

      await dispatch(uploadKYCDocument(selectedUser._id, 'pan', panData, scannedDocs.pan.image))
      await dispatch(uploadKYCDocument(selectedUser._id, 'aadhaar_front', aadhaarFrontData, scannedDocs.aadhaar_front.image))
      await dispatch(uploadKYCDocument(selectedUser._id, 'aadhaar_back', aadhaarBackData, scannedDocs.aadhaar_back.image))

      // Submit and approve - marks user as fully verified
      await dispatch(submitManualKYC(selectedUser._id))
      await dispatch(approveManualKYC(selectedUser._id))

      toast.success('KYC verified and approved successfully!')
      setSelectedUser(null)
      setActiveTab('search')
      setScannedDocs({
        pan: { image: null, uploaded: false, extractedName: null },
        aadhaar_front: { image: null, uploaded: false, extractedName: null },
        aadhaar_back: { image: null, uploaded: false, extractedName: null }
      })
      dispatch(getPendingKYC())
      dispatch(searchUsersForKYC('')) // Refresh the user list
    } catch (error) {
      toast.error('Failed to submit KYC')
    }
  }

  // View pending request details
  const handleViewRequest = (user) => {
    setViewingUser(user)
    dispatch(getKYCDetails(user._id))
  }

  // Approve pending request
  const handleApprove = async (userId) => {
    try {
      await dispatch(approveManualKYC(userId))
      toast.success('KYC approved successfully!')
      setViewingUser(null)
      dispatch({ type: GET_KYC_DETAILS_RESET })
      dispatch(getPendingKYC())
    } catch (error) {
      toast.error('Failed to approve KYC')
    }
  }

  // Reject pending request
  const handleReject = async () => {
    if (!rejectReason) {
      toast.error('Please provide a rejection reason')
      return
    }

    try {
      await dispatch(rejectManualKYC(viewingUser._id, rejectReason))
      toast.success('KYC rejected')
      setShowRejectModal(false)
      setRejectReason('')
      setViewingUser(null)
      dispatch({ type: GET_KYC_DETAILS_RESET })
      dispatch(getPendingKYC())
    } catch (error) {
      toast.error('Failed to reject KYC')
    }
  }

  const getKYCDisplayName = (record) => {
    if (!record) return 'N/A'

    const directName = typeof record.name === 'string' ? record.name.trim() : ''
    if (directName) return directName

    return (
      record?.verification?.manualKYC?.pan?.extractedData?.name ||
      record?.verification?.manualKYC?.aadhaar?.front?.extractedData?.name ||
      record?.manualKYC?.pan?.extractedData?.name ||
      record?.manualKYC?.aadhaar?.front?.extractedData?.name ||
      record?.identifier ||
      'N/A'
    )
  }

  const closeReview = () => {
    setViewingUser(null)
    dispatch({ type: GET_KYC_DETAILS_RESET })
  }

  const closeRejectModal = () => {
    setShowRejectModal(false)
    setRejectReason('')
  }

  const clearCurrentDoc = () => {
    setScannedDocs(prev => ({
      ...prev,
      [currentDocType]: { image: null, uploaded: false, extractedName: null }
    }))
  }

  const capturedCount = DOC_TYPES.filter((doc) => scannedDocs[doc.key].image).length
  const allCaptured = capturedCount === DOC_TYPES.length
  const currentDoc = scannedDocs[currentDocType]
  const currentDocLabel = DOC_TYPES.find((doc) => doc.key === currentDocType)?.label

  const reviewDocs = kycDetailsData?.manualKYC
    ? [
      { label: 'PAN card', doc: kycDetailsData.manualKYC.pan },
      { label: 'Aadhaar front', doc: kycDetailsData.manualKYC.aadhaar?.front },
      { label: 'Aadhaar back', doc: kycDetailsData.manualKYC.aadhaar?.back },
    ].filter((item) => item.doc?.scanned)
    : []

  const tabItems = [
    { value: 'search', label: 'Find player', icon: SearchIcon },
    { value: 'pending', label: 'Pending review', icon: PersonIcon, count: pendingRequests?.length || undefined },
    ...(selectedUser ? [{ value: 'scan', label: 'Scan documents', icon: ScanLine, count: `${capturedCount}/3` }] : []),
  ]

  const searchColumns = [
    {
      field: 'name',
      title: 'Player',
      render: (user) => (
        <PersonCell name={user.name || user.identifier || 'Unknown'} sub={user.name ? user.identifier : undefined} />
      ),
    },
    { field: 'phoneNumber', title: 'Phone', render: (user) => <span className="tabular-nums">{user.phoneNumber || '—'}</span> },
    {
      field: 'aadhaarVerified',
      title: 'Aadhaar',
      render: (user) => <VerifiedPill verified={user.aadhaarVerified} />,
    },
    {
      field: 'panVerified',
      title: 'PAN',
      render: (user) => <VerifiedPill verified={user.panVerified} />,
    },
    {
      field: 'manualKYCStatus',
      title: 'Manual KYC',
      render: (user) => user.manualKYCStatus
        ? <StatusPill status={capitalize(user.manualKYCStatus)} size="sm" />
        : <span className="text-cm-muted">—</span>,
    },
    {
      field: '_action',
      title: '',
      sortable: false,
      align: 'right',
      render: (user) =>
        user.aadhaarVerified && user.panVerified ? (
          <StatusPill tone="success" icon={BadgeCheck} size="sm">Verified</StatusPill>
        ) : (
          <Button
            size="xs"
            variant={selectedUser?._id === user._id ? 'outline' : 'secondary'}
            icon={ScanLine}
            aria-label={`Start KYC for ${user.name || user.identifier || 'user'}`}
            onClick={() => handleSelectUser(user)}
          >
            Start KYC
          </Button>
        ),
    },
  ]

  const pendingColumns = [
    {
      field: 'name',
      title: 'Player',
      render: (request) => <PersonCell name={getKYCDisplayName(request)} sub={request.identifier} />,
    },
    { field: 'phoneNumber', title: 'Phone', render: (request) => <span className="tabular-nums">{request.phoneNumber || '—'}</span> },
    {
      field: 'verification.manualKYC.status',
      title: 'Status',
      render: (request) => (
        <StatusPill status={capitalize(request.verification?.manualKYC?.status || 'pending')} size="sm" />
      ),
    },
    {
      field: 'verification.manualKYC.submittedAt',
      title: 'Submitted',
      render: (request) => request.verification?.manualKYC?.submittedAt
        ? new Date(request.verification.manualKYC.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        : '—',
    },
    {
      field: '_action',
      title: '',
      sortable: false,
      align: 'right',
      render: (request) => (
        <Button size="xs" variant="secondary" icon={VisibilityIcon} onClick={() => handleViewRequest(request)}>
          Review
        </Button>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-5 min-w-0">
      <ScreenHeader
        title="Manual KYC"
        description="Verify a player in person by scanning their PAN and both sides of their Aadhaar."
      />

      <Tabs items={tabItems} value={activeTab} onChange={setActiveTab} ariaLabel="Manual KYC sections" />

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="flex flex-col gap-3">
          <FilterBar>
            <SearchInput
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name, phone or email…"
              className="flex-1 sm:max-w-md"
            />
            <Button variant="secondary" icon={SearchIcon} onClick={handleSearch}>
              Search
            </Button>
          </FilterBar>
          {searchError && <Notice tone="danger" icon={CancelIcon}>{searchError}</Notice>}
          <DataTable
            columns={searchColumns}
            data={searchResults || []}
            loading={searchLoading}
            search={false}
            emptyTitle="No players found"
            emptyDescription="Try a different name, phone number or email."
          />
        </div>
      )}

      {/* Pending Tab */}
      {activeTab === 'pending' && (
        <div className="flex flex-col gap-3">
          {pendingError && <Notice tone="danger" icon={CancelIcon}>{pendingError}</Notice>}
          <DataTable
            title="Waiting for review"
            description="Submitted manual KYC that still needs approval."
            columns={pendingColumns}
            data={pendingRequests || []}
            loading={pendingLoading}
            search={false}
            toolbarRight={
              <Button variant="secondary" size="sm" icon={RefreshIcon} onClick={() => dispatch(getPendingKYC())}>
                Refresh
              </Button>
            }
            emptyTitle="Nothing to review"
            emptyDescription="New manual KYC submissions will appear here."
          />
        </div>
      )}

      {/* Scan Tab */}
      {activeTab === 'scan' && selectedUser && (
        <div className="grid gap-5 lg:grid-cols-3 items-start">
          <Card className="lg:col-span-2">
            <div className="flex items-start justify-between gap-3 mb-4">
              <PersonCell
                name={selectedUser.name || selectedUser.phoneNumber}
                sub={selectedUser.phoneNumber}
                size={40}
              />
              <IconButton
                icon={CloseIcon}
                variant="ghost"
                size="sm"
                label="Cancel KYC for this player"
                onClick={() => {
                  setSelectedUser(null)
                  setActiveTab('search')
                }}
              />
            </div>

            {/* Step selector */}
            <ol className="grid grid-cols-3 gap-2 m-0 p-0 list-none mb-4">
              {DOC_TYPES.map((doc, index) => {
                const done = Boolean(scannedDocs[doc.key].image)
                const active = currentDocType === doc.key
                return (
                  <li key={doc.key}>
                    <button
                      type="button"
                      onClick={() => setCurrentDocType(doc.key)}
                      aria-current={active ? 'step' : undefined}
                      className={`flex w-full items-center gap-2 rounded-cm-lg border px-3 py-2.5 text-left transition-colors ${active
                        ? 'border-cm-gold bg-cm-gold-soft'
                        : 'border-cm-border bg-cm-surface-2 hover:border-cm-border-strong'
                        }`}
                    >
                      <span
                        className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${done
                          ? 'bg-cm-success text-white'
                          : active ? 'bg-cm-gold text-cm-gold-ink' : 'bg-cm-surface-3 text-cm-muted'
                          }`}
                      >
                        {done ? <CheckIcon size={13} /> : index + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[13px] font-semibold text-cm-text truncate">{doc.label}</span>
                        <span className="block text-[11px] text-cm-muted">{done ? 'Captured' : 'Not captured'}</span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ol>

            {/* Capture area */}
            <div className="rounded-cm-lg border border-dashed border-cm-border-strong bg-cm-surface-2 p-4 sm:p-6">
              {ocrProcessing && (
                <Notice tone="info" className="mb-4" icon={ScanLine}>
                  <span className="inline-flex items-center gap-2">
                    <Spinner size={13} /> Reading the name from the document…
                  </span>
                </Notice>
              )}
              {currentDoc.image ? (
                <div className="flex flex-col items-center gap-4">
                  <img
                    src={currentDoc.image}
                    alt={`Captured ${currentDocLabel}`}
                    className="w-full max-w-lg rounded-cm-lg border border-cm-border object-contain bg-cm-surface"
                  />
                  {currentDoc.extractedName ? (
                    <div className="w-full max-w-lg rounded-cm-lg bg-cm-success-soft px-4 py-3">
                      <p className="text-xs font-semibold text-cm-success m-0">Name read from document</p>
                      <p className="text-base font-bold text-cm-text m-0">{currentDoc.extractedName}</p>
                    </div>
                  ) : !ocrProcessing && (
                    <Notice tone="warning" className="w-full max-w-lg" icon={CancelIcon}>
                      The name couldn't be read automatically. Check it against the document yourself.
                    </Notice>
                  )}
                  <Button variant="secondary" icon={RefreshIcon} onClick={clearCurrentDoc}>
                    Retake
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                  <span className="grid h-14 w-14 place-items-center rounded-cm-lg bg-cm-gold-soft text-cm-gold-text">
                    <CameraAltIcon size={26} />
                  </span>
                  <div>
                    <p className="text-base font-bold text-cm-text m-0">Capture {currentDocLabel}</p>
                    <p className="text-sm text-cm-muted m-0">Use the camera or upload a clear photo.</p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 mt-1">
                    <Button icon={CameraAltIcon} onClick={() => cameraInputRef.current.click()}>
                      Camera
                    </Button>
                    <Button variant="secondary" icon={UploadIcon} onClick={() => fileInputRef.current.click()}>
                      Upload
                    </Button>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleCapture}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCapture}
                className="hidden"
              />
            </div>
          </Card>

          {/* Summary */}
          <Card>
            <CardHeader
              title="Documents"
              description={allCaptured ? 'All documents captured.' : `${capturedCount} of 3 captured`}
            />
            <ul className="flex flex-col gap-2 m-0 p-0 list-none mb-4">
              {DOC_TYPES.map((doc) => {
                const scanned = scannedDocs[doc.key]
                return (
                  <li key={doc.key}>
                    <button
                      type="button"
                      onClick={() => setCurrentDocType(doc.key)}
                      className="flex w-full items-center gap-3 rounded-cm-lg border border-cm-border bg-cm-surface-2 p-2 text-left hover:border-cm-border-strong"
                    >
                      {scanned.image ? (
                        <img src={scanned.image} alt="" className="h-10 w-14 shrink-0 rounded object-cover" />
                      ) : (
                        <span className="grid h-10 w-14 shrink-0 place-items-center rounded bg-cm-surface-3 text-cm-muted">
                          <CameraAltIcon size={16} />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold text-cm-text">{doc.label}</span>
                        <span className="block text-xs text-cm-muted truncate">
                          {scanned.extractedName || (scanned.image ? 'Name not read' : 'Not captured')}
                        </span>
                      </span>
                      {scanned.image && <CheckCircleIcon size={16} className="text-cm-success shrink-0" />}
                    </button>
                  </li>
                )
              })}
            </ul>
            <Button
              block
              icon={CheckCircleIcon}
              disabled={!allCaptured || ocrProcessing}
              onClick={handleSubmitKYC}
            >
              Verify & approve KYC
            </Button>
            <p className="text-xs text-cm-muted mt-2 mb-0 text-center">
              Uploads all three documents and marks the player as verified.
            </p>
          </Card>
        </div>
      )}

      {/* Review Modal */}
      <Modal
        open={Boolean(viewingUser)}
        onClose={() => { if (!showRejectModal) closeReview() }}
        title="Review KYC"
        description={kycDetailsData
          ? [getKYCDisplayName(kycDetailsData), kycDetailsData.phoneNumber].filter((v) => v && v !== 'N/A').join(' · ')
          : undefined}
        icon={BadgeCheck}
        size="xl"
        footer={
          <>
            <Button variant="danger" icon={CancelIcon} disabled={!kycDetailsData} onClick={() => setShowRejectModal(true)}>
              Reject
            </Button>
            <Button icon={CheckCircleIcon} disabled={!kycDetailsData} onClick={() => handleApprove(viewingUser._id)}>
              Approve
            </Button>
          </>
        }
      >
        {detailsLoading || !kycDetailsData ? (
          <div className="grid gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 rounded-cm-lg" />)}
          </div>
        ) : reviewDocs.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-3">
            {reviewDocs.map(({ label, doc }) => (
              <figure key={label} className="m-0 flex flex-col rounded-cm-lg border border-cm-border bg-cm-surface-2 overflow-hidden">
                {doc.imageUrl ? (
                  <a href={resolveUploadUrl(doc.imageUrl)} target="_blank" rel="noopener noreferrer" className="block bg-cm-surface">
                    <img
                      src={resolveUploadUrl(doc.imageUrl)}
                      alt={label}
                      className="w-full h-44 object-contain"
                      onError={(event) => handleUploadImageError(event, doc.imageUrl)}
                    />
                  </a>
                ) : (
                  <div className="grid h-44 place-items-center text-sm text-cm-muted">No image</div>
                )}
                <figcaption className="p-3 border-t border-cm-border">
                  <p className="text-[13px] font-semibold text-cm-text m-0">{label}</p>
                  <p className="text-xs text-cm-muted m-0">
                    {doc.extractedData?.name ? <>Name read: <b className="text-cm-text">{doc.extractedData.name}</b></> : 'No name read'}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <Notice tone="warning" icon={CancelIcon}>No scanned documents on this request.</Notice>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        open={showRejectModal}
        onClose={closeRejectModal}
        title="Reject KYC"
        description="The player will need to submit their documents again."
        icon={CancelIcon}
        tone="danger"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={closeRejectModal}>Cancel</Button>
            <Button variant="danger" disabled={!rejectReason.trim()} onClick={handleReject}>Reject</Button>
          </>
        }
      >
        <Textarea
          label="Reason"
          required
          data-autofocus
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. Aadhaar photo is blurry"
          rows={4}
        />
      </Modal>
    </div>
  )
}

export default ManualKYC
